import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { CompanyGateway } from 'src/app/application/gateway/CompanyGateway';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { ForwarderGateway } from 'src/app/application/gateway/ForwarderGateway';
import { Company, EditCompany, ProfileCompanyList } from 'src/app/domain/models/Company';
import { Country } from 'src/app/domain/models/Country';
import { ForwarderUserCreateRequest } from 'src/app/domain/models/ForwarderReview';
import { Profile } from 'src/app/domain/models/Profile';
import { createPasswordStrengthValidator } from 'src/app/ui/util/custom-validators';
import { ErrorMessageUtil } from 'src/app/ui/util/error-message-util';

@Component({
  selector: 'app-forwarder-users',
  templateUrl: './forwarder-users.component.html',
  styleUrls: ['./forwarder-users.component.css'],

})
export class ForwarderUsersComponent implements OnInit {

  countries: Country[] = []
  selectedCountry: Country | undefined
  forwarderId: number | undefined
  forwarder: Company | undefined

  profiles: Profile[] = []
  users: ProfileCompanyList[] = []
  selectedUserAdmin: any
  selectedUserCountry: Country | undefined

  loading: boolean = false
  submit: boolean = false
  visibleDialog: boolean = false

  formGroup: FormGroup = this._formBuilder.group({
    companyName: ['', Validators.required],
    contactName: ['', Validators.required],
    contactLastName: [null, Validators.required],
    contactPhone: [null, Validators.required],
    contactEmail: ['', [Validators.required, Validators.email]],
    countryId: [null, Validators.required],
    profileId: [null]
  })

  formUserGroup: FormGroup = this._formBuilder.group({
    userContactName: ['', Validators.required],
    userContactLastName: ['', Validators.required],
    userContactEmail: ['', [Validators.required, Validators.email]],
    userCountryId: [null, Validators.required],
    userContactCity: [''],
    userContactAddress: [''],
    userContactPhone: [''],
    password: [null, [createPasswordStrengthValidator(), Validators.minLength(8)]]
  })

  constructor(
    private _formBuilder: FormBuilder,
    private _messageService: MessageService,
    private _countryService: CountryGateway,
    private _companyService: CompanyGateway,
    private _forwarderService: ForwarderGateway,
    public translate: TranslateService) {}

  ngOnInit(): void {
    this.loadCountries()
    this.loadForwarder()
  }

  loadForm() {
    if(this.forwarder) {
      this.forwarderId = this.forwarder.id
      this.formGroup.controls["companyName"].setValue(this.forwarder.companyName)
      this.formGroup.controls["contactName"].setValue(this.forwarder.contactName)
      this.formGroup.controls["contactLastName"].setValue(this.forwarder.contactLastName)
      this.formGroup.controls["contactPhone"].setValue(this.forwarder.contactPhone)
      this.formGroup.controls["contactEmail"].setValue(this.forwarder.contactEmail)
      this.formGroup.controls["countryId"].setValue(this.forwarder.countryId)

      if (this.forwarder.countryId != null) this.onChangeCountry()
    }
  }

  loadCountries(): void {
    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v
      },
      error: (e) => {
        this._messageService.add({ severity: 'error', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: this.translate.instant('ForwarderPage.Messages.ErrorLoadCountries') });
        console.log(e)
      },
      complete: () => console.info('Country query successfully')
    })
  }

  loadForwarder(): void {
    this._companyService.getUsersCompanyByProfile().subscribe({
      next: (v) => {
        this.forwarder = v
        if (this.forwarder.users) this.users = this.forwarder.users
        this.loadForm()
      },
      error: (e) => {
        let errorMessage = ''
        const lang = this.translate.currentLang
        switch(lang) {
          case 'es':
            errorMessage = e.error.messageEs
            break
          case 'en':
            errorMessage = e.error.messageEn
            break
          default:
            errorMessage = this.translate.instant('ForwarderPage.Messages.ErrorLoadForwarders')
        }
        console.log(e)
        this._messageService.add({ severity: 'error', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: errorMessage });
      },
      complete: () =>  {
        console.info('Forwarder query successfully')
      }
    })
  }

  getErrorMessage(formGroup: FormGroup, fieldName: string): string {
    return ErrorMessageUtil.getErrorMessage(formGroup, this.translate, fieldName);
  }

  editCompany(): void{
    this.submit = true
    if(this.formGroup.valid) {
      const company: EditCompany = {
        id: this.forwarderId,
        companyName: this.formGroup.controls['companyName'].value,
        contactName: this.formGroup.controls['contactName'].value,
        contactLastName: this.formGroup.controls['contactLastName'].value,
        contactPhone: this.formGroup.controls['contactPhone'].value,
        contactEmail: this.formGroup.controls['contactEmail'].value,
        countryId: this.formGroup.controls['countryId'].value,
        companyTypeId: 0, // Forwarder
        users: this.users.map((item) => { return { ...item, company: this.forwarder}})
      }

      this.loading = true
      this._companyService.update(company).subscribe({
        next: (v) => {
          this.loading = false
          this.submit = false
          this._messageService.add({ severity: 'success', summary: this.translate.instant('MessageService.Summary.SuccessAction'), detail: this.translate.instant('ForwarderPage.Messages.SuccessEditForwarder') });
        },
        error: (e) => {
          this.loading = false
          this.submit = false
          this._messageService.add({ severity: 'error', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: this.translate.instant('ForwarderPage.Messages.ErrorAddedForwarder') });
          console.log(e)
        },
        complete: () => console.info('Forwarder created successfully')
      })
    } else {
      this.findInvalidControls()
      this._messageService.add({ severity: 'error',
        summary: this.translate.instant('MessageService.Summary.FailedAction'),
        detail: this.translate.instant('MessageService.FormMessages.FormInvalid') })
    }
  }

  onChangeCountry() {
    this.selectedCountry = this.countries.find(x => x.id === this.formGroup.controls['countryId'].value)
  }

  onChangeUserCountry() {
    this.selectedUserCountry = this.countries.find(x => x.id === this.formUserGroup.controls['userCountryId'].value)
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.formGroup.controls;
    for (const name in controls) {
        if (controls[name].invalid) {
            invalid.push(name);
        }
    }
    return invalid;
  }

  onSelectAdmin(selected: ProfileCompanyList) {
    this.selectedUserAdmin = selected.profile.id

    this.users.forEach(user => {
      user.isAdmin = user.profile.id === this.selectedUserAdmin;
    });
  }

  addForwarder() {
    let profile = this.formGroup.controls['profileId'].value
    if (profile)
    {
      if (this.users.some((item) => item.profile.id === profile.id))
      {
        this._messageService.add({ severity: 'info', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: this.translate.instant('ForwarderPage.Messages.ErrorExistingForwarder') });
        return;
      }

      let newUser: ProfileCompanyList = {
        isAdmin: false,
        profile: profile,
        id: 0,
        companyId: this.forwarderId ?? 0,
        profileId: profile.id
      }
      this.users.push(newUser)
    }
  }

  removeUser(idx: number) {
    this.users.splice(idx, 1)
  }

  saveUser() {
    this.submit = true
    if(this.formUserGroup.valid && this.forwarderId) {
      const profile: ForwarderUserCreateRequest = {
        name: this.formUserGroup.controls['userContactName'].value,
        lastName: this.formUserGroup.controls['userContactLastName'].value,
        email: this.formUserGroup.controls['userContactEmail'].value,
        companyName: this.formGroup.controls['companyName'].value,
        contactCountryId: this.formUserGroup.controls['userCountryId'].value,
        contactAddress: this.formUserGroup.controls['userContactAddress'].value,
        contactCity: this.formUserGroup.controls['userContactCity'].value,
        contactPhone: this.formUserGroup.controls['userContactPhone'].value,
        companyId: this.forwarderId,
        password: this.formUserGroup.controls['password'].value
      }

      this.loading = true
      this._forwarderService.createUser(profile).subscribe({
        next: (v) => {
          this.loading = false
          this.submit = false
          this._messageService.add({ severity: 'success', summary: this.translate.instant('MessageService.Summary.SuccessAction'), detail: this.translate.instant('ForwarderPage.Messages.SuccessAddedForwarder') });
          this.loadForwarder()
          this.visibleDialog = false
        },
        error: (e) => {
          console.error(e);
          this.loading = false
          this.submit = false
          this._messageService.add({ severity: 'error', summary: this.translate.instant('MessageService.Summary.FailedAction'), detail: this.translate.instant('ForwarderPage.Messages.ErrorAddedForwarder') });
          console.log(e)
        },
        complete: () => console.info('Profile created successfully')
      })
    }
    // else{
    //   this.findInvalidControls()
    // }
  }



}
